import { CheckCheck } from "lucide-react";
import { type ReactNode } from "react";
import { type Meeting, type MeetingProposal, type PipelineRun, type Entity, type Transcript } from "@/lib/api";
import { formatElapsed } from "@/lib/format";
import { useAudioFragment } from "@/lib/useAudioFragment";
import { type PipelineRunState } from "@/lib/usePipelineRun";
import { DebugTab } from "@/pages/DebugTab";
import { RestartControl } from "@/pages/RestartControl";
import { MeetingsPhase } from "@/pages/recording/MeetingsPhase";
import { ProposedMeetings } from "@/pages/recording/ProposedMeetings";
import { SpeakersTab } from "@/pages/recording/SpeakersTab";

/**
 * "Pipeline complete — 4 steps, 11m 49s on GPU-WORKSTATION-01", and the control that
 * runs it again from a step (SPCH-10).
 *
 * Each part of the line is left out when it is not known rather than filled in with a
 * dash: an older run may have no worker name on any step, and a run whose completion was
 * never recorded has no elapsed time.
 */
export function CompletePanel({
  run,
  sourceId,
  state,
  elapsedMs,
  worker,
  onChanged,
}: {
  run: PipelineRun;
  sourceId: string;
  state: PipelineRunState;
  elapsedMs: number | null;
  worker: string | null;
  onChanged: () => void;
}) {
  const detail = [
    `${run.steps.length} step${run.steps.length === 1 ? "" : "s"}`,
    elapsedMs !== null && elapsedMs >= 0 ? formatElapsed(elapsedMs) : "",
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-6)] py-[var(--space-4)] backdrop-blur-[var(--blur-sm)]">
      <div className="flex flex-wrap items-center justify-between gap-[var(--space-3)]">
        <p className="flex items-center gap-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]">
          <CheckCheck size={16} strokeWidth={2.5} className="shrink-0 text-[var(--success)]" aria-hidden />
          Pipeline complete — {detail}
          {worker && ` on ${worker}`}
        </p>

        <RestartControl run={run} sourceId={sourceId} state={state} onChanged={onChanged} />
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ recording wizard

/**
 * What a recording's audio phase hands the owner, in the order there is anything to do
 * about it: confirm who was speaking, then decide how its timeline divides into the
 * meetings the content phase (correct_transcript, summarize, extract_insights,
 * tag_entities, relations) will actually run over. Two tabs read as two unrelated views;
 * this is one path with a destination, which is what it is.
 *
 * Not a hard gate — nothing here stops a reader opening step 2 before step 1 is done,
 * because nothing downstream requires that order either (a meeting can be split, or even
 * started, before every voice has a name). The numbering is a recommendation, and the
 * per-step progress line is how a reader tells whether they are done without being told
 * they must be.
 */
export function RecordingWizard({
  transcript,
  player,
  persons,
  onConfirmSpeaker,
  meetings,
  onMeetingsChanged,
  proposal,
  onProposalApplied,
  run,
  recordingId,
  startedAt,
  developerMode,
}: {
  transcript: Transcript | null;
  player: ReturnType<typeof useAudioFragment>;
  persons: Entity[];
  onConfirmSpeaker: (speakerId: string, who: { personId: string } | { name: string }) => Promise<void>;
  meetings: Meeting[] | null;
  onMeetingsChanged: (updated: Meeting) => void;
  proposal: MeetingProposal | null;
  onProposalApplied: (meetings: Meeting[]) => void;
  run: PipelineRun;
  recordingId: string;
  startedAt: string | null;
  developerMode: boolean;
}) {
  const speakers = transcript?.speakers ?? [];
  const confirmedCount = speakers.filter((speaker) => speaker.status === "confirmed").length;

  return (
    <div className="flex flex-col gap-[var(--space-2)]">
      <WizardStep
        step={1}
        title="Name the speakers"
        status={speakers.length > 0 ? `${confirmedCount} of ${speakers.length} confirmed` : undefined}
      >
        <SpeakersTab transcript={transcript} player={player} persons={persons} onConfirm={onConfirmSpeaker} />
      </WizardStep>

      <WizardStep
        step={2}
        title="Split into meetings, then start them"
        status={
          proposal?.status === "ready" && proposal.awaitingAdoption === true
            ? "Approve the proposed meetings"
            : meetingsStatus(meetings)
        }
        last
      >
        <div className="flex flex-col gap-[var(--space-4)]">
          {proposal && proposal.status !== "unavailable" && (
            <ProposedMeetings
              recordingId={recordingId}
              proposal={proposal}
              speakers={speakers}
              segments={transcript?.segments ?? []}
              meetings={meetings}
              onApplied={onProposalApplied}
              player={player}
              startedAt={startedAt}
            />
          )}
          <MeetingsPhase
            meetings={meetings}
            onChanged={onMeetingsChanged}
            awaitingProposal={proposal?.status === "ready" && proposal.awaitingAdoption === true}
          />
        </div>
      </WizardStep>

      {developerMode && (
        <details className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)] backdrop-blur-[var(--blur-sm)]">
          <summary className="cursor-pointer text-[length:var(--text-sm)] font-semibold text-[var(--text-secondary)]">
            Debug — the audio phase's own run
          </summary>
          <div className="mt-[var(--space-4)]">
            <DebugTab run={run} recordingId={recordingId} />
          </div>
        </details>
      )}
    </div>
  );
}

/** "1 of 3 started", read off whatever the meetings list has loaded so far. */
function meetingsStatus(meetings: Meeting[] | null): string | undefined {
  if (!meetings || meetings.length === 0) return undefined;

  const started = meetings.filter((meeting) => meeting.status !== null).length;
  return `${started} of ${meetings.length} started`;
}

/**
 * One numbered step of the wizard: a circle (its number, connected by a line to the next
 * step unless it is the last), a title, an optional one-line progress summary, and
 * whatever the step actually asks the reader to do.
 */
function WizardStep({
  step,
  title,
  status,
  last,
  children,
}: {
  step: number;
  title: string;
  status?: string;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="flex gap-[var(--space-4)]">
      <div className="flex shrink-0 flex-col items-center">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-full)] border border-[var(--border-default)] bg-[var(--surface-glass)] text-[length:var(--text-sm)] font-bold text-[var(--text-secondary)]">
          {step}
        </span>
        {!last && <span className="mt-1 w-px flex-1 bg-[var(--border-subtle)]" aria-hidden />}
      </div>

      <div className="min-w-0 flex-1 pb-[var(--space-6)]">
        <div className="flex flex-wrap items-baseline gap-x-[var(--space-3)] gap-y-1">
          <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">{title}</h2>
          {status && <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">{status}</p>}
        </div>
        <div className="mt-[var(--space-3)]">{children}</div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ transcript
